import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { AccountsContextService } from '../../accounts/account-context.service';
import { AclService } from '../acl.service';
import { AclResources } from '../../shared/types/acl.types';

@Injectable({
  providedIn: 'root',
})
export class AclToolsService {

  public readonly aclService: AclService;
  private readonly ctxService: AccountsContextService;

  constructor() {
    this.aclService = inject(AclService);
    this.ctxService = inject(AccountsContextService);
  }

  public createIsAllowedFor(requiredResources$: Observable<AclResources>): Observable<boolean> {
    return combineLatest([
      this.aclService.aclMapGeneral$,
      this.aclService.aclMapByAccount$,
      this.ctxService.account$,
      requiredResources$,
    ])
      .pipe(
        map(([,,, requiredResources]) => this.isAllowedForInstant(requiredResources))
      );
  }

  public isAllowedFor(requiredResources: AclResources, isSharedObservable: boolean = true): Observable<boolean> {
    let observable$ = this.createIsAllowedFor(of(requiredResources));
    if (isSharedObservable) {
      observable$ = observable$.pipe(shareReplay());
    }

    return observable$;
  }

  public isAllowedForInstant(requiredResources: AclResources, accountId?: string): boolean {
    const aclMapGeneral = this.aclService.aclMapGeneralSnapshot;
    const aclMapByAccount = this.aclService.aclMapByAccountSnapshot;
    accountId = accountId ?? this.ctxService.accountSnapshot!.id!;

    return requiredResources.every(resource => aclMapGeneral?.[resource] || aclMapByAccount?.[accountId]?.[resource]);
  }

  public isAllowedForOrganizationsInstant(requiredResources: AclResources, orgId: string): boolean {
    const aclMapByOrg = this.aclService.aclMapByOrgSnapshot;
    return requiredResources.every(resource => aclMapByOrg?.[orgId]?.[resource]);
  }
}
